import styled, { css } from 'styled-components'

import { media } from 'featureSliced/shared/utils/breakpoints'
import { getContrastColor } from 'featureSliced/shared/utils/getContrastColor'

type SecondaryImageProps = {
  isActive?: boolean
}

type KitchenItemProps = {
  isActive: boolean
}

// ProductMain

export const ProductMainContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-height: calc(85vh - 198px);
  overflow-y: scroll;
  //
  width: calc(100% + 40px);
  margin-bottom: 40px;
  margin-left: -20px;
  margin-top: -25px;
  //
  & > p {
    width: 100%;
  }

  /* QRM-641: in the web dialog the image sits BESIDE the details instead of as a
     stretched hero on top — the stored source is 256x256, so a full-width hero
     is an upscale. The phone bleed (negative margins, viewport-height cap) has
     to go with it, or the body is pushed out of the dialog. */
  ${media.web} {
    flex-direction: row;
    align-items: flex-start;
    gap: 24px;
    max-height: none;
    overflow-y: visible;
    width: 100%;
    margin: 0 0 8px;
  }
`

export const ProductMainContainerInner = styled.div`
  width: calc(100vw - 40px);
  margin-bottom: 40px;
  /* width: 100%; */

  ${media.web} {
    width: auto;
    flex: 1;
    min-width: 0;
    margin-bottom: 0;
    text-align: left;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
`

// Image

export const ProductMainImageContainer = styled.div`
  min-height: 260px;
  height: 260px;
  min-width: 100%;
  width: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  /* exactly the stored resolution — sharp without touching the image pipeline */
  ${media.web} {
    flex: 0 0 256px;
    width: 256px;
    min-width: 256px;
    height: 256px;
    min-height: 256px;
    border-radius: 10px;
    overflow: hidden;
  }
`

export const ProductMainImage = styled.img`
  position: absolute;
  object-fit: contain;
  width: 100%;
  height: 260px;

  /* stays absolute: the wishlist/note buttons are siblings and are meant to sit
     ON the image — making it static pushed them above it and squashed the photo */
  ${media.web} {
    width: 100%;
    height: 100%;
  }
`

// No-image placeholder: grey fills the whole image area (like a cover photo);
// the camera glyph scales to fill it, centered (aspect preserved — no distortion).
export const PlaceholderImage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #fafafa;

  & svg {
    width: 100%;
    height: 100%;
  }
`

export const SecondaryImage = styled('div')<SecondaryImageProps>`
  display: flex;
  justify-content: center;
  align-items: center;

  height: 40px;
  width: 40px;
  border-radius: 50%;

  color: #fff;
  z-index: 4;
  cursor: pointer;
  background-color: ${({ isActive }) =>
    isActive ? '#ff5151' : 'rgba(0, 0, 0, 0.55)'};
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.22);
  img {
    width: 18px;
    height: auto;
    /* the glyph must never swallow the tap — the handler lives on the circle */
    pointer-events: none;
  }
`

export const AgeLabel = styled.div`
  align-self: flex-start;
  height: 24px;
  padding: 0 8px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 4;
  background-color: rgba(0, 0, 0, 0.55);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.22);
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;

  /* line up with the 40px control circles it stacks under */
  ${media.web} {
    align-self: stretch;
    width: 40px;
    padding: 0;
  }
`

export const ButtonsContainer = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;

  display: flex;
  flex-direction: column;
  gap: 5px;
`

// Title And Price

export const TitleAndPriceContainer = styled.div`
  font-weight: 600;
  font-size: 24px;
  line-height: 30px;
  min-height: 48px;
  margin: 20px 0 15px;
  /* margin-bottom: 10px; */
  /* padding-right: 135px;
  padding-bottom: 24px; */
  color: ${({ theme }) => theme.FontColor};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  position: relative;
`

export const TitleColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  min-width: 0;
`

export const InfoCircle = styled.button`
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.FontColor};
  background: transparent;
  color: ${({ theme }) => theme.FontColor};
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  svg {
    width: 20px;
    height: 20px;
    display: block;
  }
`

export const ProductTitlePrice = styled('p')<any>`
  /* position: absolute;
  right: 0;
  bottom: 0; */
  font-size: 18px;
  line-height: 22px;
  /* RED COLOR */
  color: ${({ theme, isDiscounted }) =>
    isDiscounted ? '#fd2724' : theme.colour};
  margin-top: 5px;
`

export const MinMaxWarning = styled(ProductTitlePrice)`
  left: 0;
  right: unset;
  font-weight: 600;
  font-size: 18px;
  line-height: 22px;
`

export const LeadDaysWarning = styled('p')`
  color: #fd2724;
  font-size: 14px;
  line-height: 18px;
  font-weight: 500;
  margin: 8px 0 0;
`

// export const ProductTotalPrice = styled.p`
//   margin: 0;
//   font-weight: 600;
//   font-size: 18px;
//   line-height: 22px;
//   color: ${({ theme }) => theme.colour};
// `

// UnitSellCodePrice

export const UnitSellCodePriceContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  position: relative;
  color: ${({ theme }) => theme.FontColor};
  & > p {
    color: ${({ theme }) => theme.FontColor};
    margin-right: 5px;
    font-size: 12px;
    width: auto;
  }
  p:nth-child(3) {
    font-weight: 600;
  }
`

export const PriceInfoContainer = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
`

export const ProductSellCodePrice = styled.p`
  margin: 0;
  font-weight: 600;
  font-size: 18px;
  line-height: 22px;
  color: ${({ theme }) => theme.colour};
`

export const CutDisclaimer = styled.p`
  font-size: 12px;
  line-height: 16px;
  margin: 8px 0 0;
  color: ${({ theme }) => theme.FontColor};
`

// Kitchen Info

export const KitchenInfoTitle = styled.p`
  font-weight: 600;
  font-size: 16px;
  line-height: 20px;
  color: ${({ theme }) => theme.FontColor};
  margin-bottom: 10px;
`

export const KitchenItemsContainer = styled.div`
  display: flex;
  flex-direction: row;
  overflow-x: scroll;
  width: 100%;
  margin-bottom: 20px;
  &::-webkit-scrollbar {
    height: 0px;
  }
`

export const KitchenItem = styled('div')<KitchenItemProps>`
  box-sizing: border-box;
  color: ${({ theme, isActive }) =>
    isActive ? getContrastColor(theme.colour) : theme.FontColor};
  background-color: ${({ theme, isActive }) =>
    isActive ? theme.colour : 'transparent'};
  border: 1px solid
    ${({ theme, isActive }) => (isActive ? theme.colour : theme.ItemsBorders)};
  padding: 10px 15px;
  margin-right: 10px;
  border-radius: 5px;
  text-align: center;
  line-height: 100%;
  display: flex;
  align-items: center;
  cursor: pointer;
`

// Allergens

export const AllergensSections = styled.div`
  display: flex;
  flex-direction: column;
`

export const AllergensTitle = styled.p`
  font-weight: 600;
  font-size: 16px;
  line-height: 20px;
  color: ${({ theme }) => theme.FontColor};
  margin-bottom: 10px;
`

export const AllergensContainer = styled.div`
  display: flex;
  overflow-x: scroll;
  margin: 0;
  margin-bottom: 10px;
  position: relative;

  /* all allergens visible at once — the phone carousel's hidden horizontal
     scroll swallowed everything past the third chip (QRM-649) */
  ${media.web} {
    flex-wrap: wrap;
    overflow-x: visible;
    gap: 8px 0;
  }
`

export const AllergenItem = styled.div<{ $iconOnly?: boolean }>`
  display: flex;
  align-items: center;
  position: relative;
  p {
    margin-right: 25px;
    white-space: nowrap;
  }

  ${({ $iconOnly }) =>
    $iconOnly &&
    css`
      &:hover::after,
      &:focus-within::after {
        content: attr(data-tooltip);
        position: absolute;
        bottom: calc(100% + 6px);
        left: 50%;
        transform: translateX(-50%);
        background: rgba(0, 0, 0, 0.8);
        color: #fff;
        padding: 4px 8px;
        border-radius: 4px;
        font-size: 12px;
        white-space: nowrap;
        z-index: 5;
        pointer-events: none;
      }
    `}
`

export const Allergen = styled.img`
  background: #ff5151;
  height: 34px;
  width: 34px;
  object-fit: cover;
  margin-right: 7px;
  border-radius: 5px;
`

export const AllergenText = styled.p`
  color: ${({ theme }) => theme.FontColor};
`

// Description

// export const DescriptionTitle = styled.p`
//   font-weight: 600;
//   font-size: 16px;
//   line-height: 20px;
//   color: ${({ theme }) => theme.FontColor};
//   margin-bottom: 10px;
// `

export const DescriptionP = styled.p`
  /* margin-bottom: 20px; */
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: ${({ theme }) => theme.FontColor};
`

// Footer

// bottom is tuned to the footer height so the fade line sits ABOVE the footer:
// 103px for the one-row footer (58px), 171px for the stacked one (56 + 12 + 58)
export const Divider = styled.div<{ stacked?: boolean }>`
  width: 100%;
  position: absolute;
  left: 0;
  bottom: ${({ stacked }) => (stacked ? '171px' : '103px')};
  border-bottom: 1px solid #cdcdcd;
  height: 20px;
  background-image: linear-gradient(
    transparent,
    ${({ theme }) => theme.MainBackGround}
  );

  /* QRM-641: absolute + bottom:103px puts the fade line wherever the phone sheet
     ends. In the web dialog the body is short and the image is 256px tall, so the
     line landed across the photo. There is nothing to fade here either — the
     dialog does not scroll — so it becomes a plain separator in the flow. */
  ${media.web} {
    position: static;
    bottom: auto;
    height: 0;
    margin-top: 16px;
    background-image: none;
    border-bottom: 1px solid ${({ theme }) => theme.ItemsBorders};
  }
`

// CTA row of a weight product already in the bucket (QRM-649): the red delete
// button sits left of the update button. Stacked = the dual-stepper's own grid,
// so the button lands exactly under the coarse ("-100g") cell.
export const FooterActions = styled.div<{ stacked?: boolean }>`
  display: grid;
  grid-template-columns: ${({ stacked }) =>
    stacked ? '1fr 1fr 88px 1fr 1fr' : '56px 1fr'};
  gap: 8px;
  width: 100%;

  & > *:last-child {
    grid-column: 2 / -1;
  }
`

export const DeleteFromBucketButton = styled.button`
  height: 100%;
  min-height: 56px;
  border: none;
  border-radius: 5px;
  background: #ff5151;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  img {
    width: 20px;
    height: 20px;
  }
`

// stacked (QRM-524): the dual-step weight stepper needs the full row width,
// so the stepper and the Add button stack vertically instead of sharing one row
export const FooterContainer = styled.div<{ stacked?: boolean }>`
  margin-top: 20px;
  height: ${({ stacked }) => (stacked ? 'auto' : '58px')};
  width: 100%;
  display: grid;
  grid-template-columns: ${({ stacked }) => (stacked ? '1fr' : '1fr 1fr')};
  grid-gap: ${({ stacked }) => (stacked ? '12px' : '21px')};
`
